import { KeyRound, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { authApi } from '@/api/client';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useAuthStore } from '@/stores/authStore';

export function LoginPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !token) {
      navigate('/');
    }
  }, [isAuthenticated, token, navigate]);

  useEffect(() => {
    if (!token) return;

    const verifyToken = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authApi.verifyMagicLink(token);
        setAuth(response.token, {
          id: response.admin.id,
          telegram_user_id: response.admin.telegram_user_id,
          telegram_username: response.admin.telegram_username,
          role: response.admin.role as 'owner' | 'admin' | 'maintainer',
          notify_channels: response.admin.notify_channels,
        });
        navigate('/');
      } catch (_err) {
        setError('Недействительная или истёкшая ссылка. Запросите новую через /login в боте.');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, navigate, setAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="cyber-card p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <h1 className="font-display text-3xl text-neon-cyan tracking-wider">
            KINO
            <span className="text-white">HELPDESK</span>
          </h1>
          <p className="text-gray-500 mt-2">Панель управления</p>
        </div>

        {loading ? (
          <div className="py-8">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <p className="text-gray-400">Проверка ссылки...</p>
          </div>
        ) : error ? (
          <div className="py-8">
            <div className="w-16 h-16 rounded-full bg-neon-red/10 border border-neon-red/30 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-neon-red" />
            </div>
            <p className="text-neon-red mb-4">{error}</p>
            <p className="text-gray-500 text-sm">
              Используйте команду <code className="text-neon-cyan">/login</code> в Telegram боте для
              получения новой ссылки.
            </p>
          </div>
        ) : !token ? (
          <div className="py-8">
            <div className="w-16 h-16 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-neon-cyan" />
            </div>
            <p className="text-gray-400 mb-4">Для входа используйте команду:</p>
            <code className="inline-block px-4 py-2 bg-cyber-gray rounded text-neon-cyan">
              /login
            </code>
            <p className="text-gray-500 text-sm mt-4">
              Отправьте эту команду в Telegram бот и перейдите по полученной ссылке.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
